import { maybe, Maybe, rd, RemoteData } from '@passionware/monads';
import { ensureError } from '@passionware/platform-js';
import { useCallback, useEffect, useState } from 'react';

export type OptimisticStateConfig<Entity, Action> = {
  reducer: (state: Entity, action: Action) => Entity;
};

type ScheduledAction<Action> = {
  correlationId: string;
  payload: Action;
  state: RemoteData<void>;
};

/**
 * Creates a custom React hook for managing optimistic updates to an entity's state.
 *
 * This factory function returns a hook that allows you to optimistically apply actions to an entity
 * before the server confirms the update. It manages a queue of scheduled actions, applies them
 * optimistically to the entity, and updates their state as the server responds.
 *
 * @template Id - The type of the entity's unique identifier.
 * @template Entity - The type of the entity being managed.
 * @template Action - The type of actions that can be performed on the entity.
 *
 * @param config - Configuration object containing a reducer function that describes how to apply an action to the entity.
 * @returns A hook that takes an options object with:
 *   - id: The entity's identifier (Maybe<Id>).
 *   - entity: The current RemoteData-wrapped entity.
 *   - executeAction: A function to execute an action on the entity, returning a promise.
 *
 * The returned hook provides optimistic state management for the entity, applying actions locally
 * while awaiting server confirmation, and updating the UI accordingly.
 */
export function createUseOptimisticState<Entity, Action>(
  config: OptimisticStateConfig<Entity, Action>
) {
  return (options: {
    id: Maybe<unknown>;
    entity: RemoteData<Entity>;
    /**
     * A function that can execute an action on an entity.
     * It should resolve when pending operation can be considered complete.
     * It's important that the next useEntity call will return the updated entity.
     * @param id A unique identifier for the entity.
     * @param action The action to execute on the entity.
     * @returns A promise that resolves when the action is complete.
     */
    executeAction: (action: Action) => Promise<void>;
  }) => {
    const [actions, setActions] = useState<ScheduledAction<Action>[]>([]);

    // Reset actions when ID changes to avoid applying actions to wrong entity
    useEffect(() => {
      setActions([]);
    }, [options.id]);

    const optimisticEntity = rd.useMemoMap(
      options.entity,
      (entity, actions) => {
        return (
          actions
            // we don't want to apply actions that have failed
            // if we do, then just not filter them out
            .filter(action => rd.isAwaiting(action.state))
            .reduce(
              (entity, action) => config.reducer(entity, action.payload),
              entity
            )
        );
      },
      actions
    );

    const updateActionState = useCallback(
      (correlationId: string, state: RemoteData<void>) => {
        setActions(actions =>
          actions.map(action =>
            action.correlationId === correlationId
              ? { ...action, state }
              : action
          )
        );
      },
      []
    );

    const removeAction = useCallback((correlationId: string) => {
      setActions(actions =>
        actions.filter(action => action.correlationId !== correlationId)
      );
    }, []);

    return {
      entity: optimisticEntity,
      actions,
      numPendingActions: actions.filter(action => rd.isAwaiting(action.state))
        .length,
      errors: actions
        .map(action => rd.tryGetError(action.state))
        .filter(maybe.isPresent),
      scheduleAction: async (correlationId: string, action: Action) => {
        setActions(actions => {
          // Throw error if correlationId already exists
          if (actions.some(a => a.correlationId === correlationId)) {
            throw new Error(
              `Action with correlationId "${correlationId}" already exists`
            );
          }
          return [
            ...actions,
            {
              correlationId,
              payload: action,
              state: rd.ofPending()
            }
          ];
        });
        try {
          await options.executeAction(action);
          removeAction(correlationId);
        } catch (error) {
          updateActionState(correlationId, rd.ofError(ensureError(error)));
        }
      }
    };
  };
}
